import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { QRCodeSVG } from 'qrcode.react';
import md5 from 'crypto-js/md5'
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import { isScheduledArticle, scheduledLabel } from '../../lib/articleSchedule';
import fields from '../../lib/fields';
import { shouldRefreshOnReturn } from '../../lib/returnRefresh';
import { openArticle } from '../../lib/articleLinks';
import { getVisibilityRefreshPromise } from '../../lib/fetchApiInterceptor';
import { isNetworkErrorEnvelope } from '../../lib/offlineCache';
import PubSubJS from 'pubsub-js';
import * as offlineWriteQueue from '../../lib/offlineWriteQueue';
import { discardImages } from '../../lib/offlineImageQueue';
import { drainQueues } from '../../lib/offlineDrain';
import { createFeedStateClient } from '../../lib/feedState';
import { compareCursorKeys } from '../../lib/feedCursor';
import {
    feedItemKey,
    itemCursorKey,
    markNewItems,
    summarizeNewItems,
    mergeEngagementWithoutNewRows,
    mergePendingPage,
} from '../../lib/feedNewItems';
import { FeedTelemetry } from '../../lib/feedTelemetry';
import { openUserProfileDrawer } from '../../lib/userProfile';
import {
    emptyFeedPreference,
    parseFeedPreference,
    isTopicFollowed,
    updateFeedPreference,
    articlePreferenceReasons,
    loadFeedPreference,
} from '../../lib/feedPreference';

import FormFields from '../../components/form/fields';
import Input from '../../components/form/input';
import ImageScroller from '../../components/imagescroller';
import InspectionStatus from '../../components/inspection/status';
import GuiList from '../../components/gui/list';
import GuiListTicker from '../../components/gui/listTicker';
import GuiListLarge from '../../components/gui/listLarge';
import GuiListFrontpage from '../../components/gui/listFrontpage';
import Sparkline from '../../components/gui/sparkline';
import Avatar from '../../components/gui/avatar';
import ArticleFilterBar from './filterBar';
import Poll from '../../components/poll';
import ResultsList from '../articleRender/resultsList';
import MapFeedPreview from '../map/feedPreview';
import FactBox from '../articleRender/factBox';
import ReadAlsoBox from '../articleRender/readAlsoBox';
import { splitBodySegments } from '../articleRender/articleContentProcessor';
import {
    isBackgroundPost,
    isStoryPost,
    usesPerLineSizes,
    parseBackgroundLines,
    lineFontSize,
    stripSizeMarkers,
} from '../../lib/backgroundPostText';
import ProductBox from '../articleRender/productBox';
import { linkifyMentions } from '../../lib/mentions';
import FishCatchCard from '../fishCatch/card';
import FishContactCard from '../fishContact/card';
import SaveButton from './saveButton';
import FollowButton from './followButton';

const ICON_SIZE = '35px';

const MARKDOWN_OPTIONS = {
    pedantic: false,
    gfm: true,
    breaks: true,
    sanitize: false,
    smartLists: true,
    smartypants: true,
    xhtml: true,
highlight: function(code, lang) {
    const hljs = require('highlight.js');
    const language = hljs.getLanguage(lang) ? lang : 'plaintext';
    return hljs.highlight(code, { language }).value;
  },
langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

// «Vis planlagte» is a per-viewer convenience, remembered in localStorage.
const SHOW_SCHEDULED_KEY = 'feedShowScheduled';
function readShowScheduled() {
    try {
        return localStorage.getItem(SHOW_SCHEDULED_KEY) === '1';
    } catch (e) {
        return false;
    }
}
function writeShowScheduled(value) {
    try {
        localStorage.setItem(SHOW_SCHEDULED_KEY, value ? '1' : '0');
    } catch (e) {
        // Storage may be unavailable (private mode); the toggle still works for the session.
    }
}

function displayImg(img, props, size = '150x') {
    if (typeof img !== 'object') {
        return undefined;
    }
    const imgSrc = `${img.s3MediumLink}`;
    return imgSrc;
}

function mergeArrays(array1, array2) {
    const resultArray = array1.slice(); // Create a copy of array1 to avoid mutating the original

    for (let i = 0; i < array2.length; i++) {
        if (i < array1.length) {
            resultArray[i] = array2[i];
        } else {
            resultArray.push(array2[i]); // Add any additional items from array2 not present in array1
        }
    }
    return resultArray;
}

function formatReadTime(ms) {
    if (!ms || ms <= 0) return null;
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) {
        return `${hours}h ${minutes % 60}m`;
    }
    if (minutes > 0) {
        return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
}

@withText(props => ({
    titleText: <Text id='article.title'>Article</Text>,
}))
@observer
class ArticleList extends Component {
  	constructor(props) {
        super(props);
        this.state = {
            limit: 50,
            offset: 0,
            totalArticles: 0,
            localList: [],
            filterByTags: [],
            selectedAuthor: null,
            showScheduled: readShowScheduled(),
            openMenuId: null,
            showBlockConfirm: null,
            blockUserId: null,
            blockUserName: '',
            blockFeatures: ['articles'],
            offlineData: false,
            pendingPosts: [],
            pendingRefreshList: null,
            pendingNewCount: 0,
            pendingNewCapped: false,
            savedArticleIds: new Set(),
            feedPreference: emptyFeedPreference(),
            preferenceLoaded: false,
            loading: true,
        };

        // Refresh-on-return bookkeeping (instance fields, not state: none of
        // these should trigger a re-render).
        this.hiddenAt = null;
        this.lastReturnRefreshAt = 0;
        this.returnRefreshInProgress = false;
        this.feedAcknowledgement = null;
        this.feedStateInitialized = false;
        this.removedSavedItems = new Map();
        this.personalizedLoadEmitted = false;
        this.isUnmounted = false;
    }

    isReturnFoundationEnabled = () => {
        const { currentCustomer = {} } = this.props.stores.appState;
        return parseInt(currentCustomer?.featureFlags?.feedReturnFoundation, 10) === 1;
    }

    isPersonalizationEnabled = () => {
        const { currentCustomer = {} } = this.props.stores.appState;
        return parseInt(currentCustomer?.featureFlags?.feedPersonalization, 10) === 1;
    }

    isPersonalizableArticle = (article = {}) => {
        const fallbackCategory = Array.isArray(this.props.category) ? null : this.props.category;
        return article._feedType !== 'news' && (article.category || fallbackCategory) === 'feed';
    }

    preferenceSurface = () => {
        if (this.props.personalized) return 'personalized';
        if (this.props.savedOnly) return 'saved';
        return 'feed';
    }

    isCanonicalFeed = (props = this.props) => {
        const {
            category, includeRaceNews, refreshOnReturn, uuidv4, articleId, savedOnly, personalized,
        } = props;
        return category === 'feed'
            && includeRaceNews === true
            && refreshOnReturn === true
            && !uuidv4
            && !articleId
            && !savedOnly
            && !personalized
            && this.state.filterByTags.length === 0
            && !this.state.selectedAuthor;
    }

    shouldRequestSaves = (props = this.props) => {
        if (!this.isReturnFoundationEnabled()) return false;
        const categories = Array.isArray(props.category) ? props.category : [props.category];
        return Boolean(props.savedOnly || categories.some(category => ['feed', 'marketplace'].includes(category)));
    }

    articleAddData = (props = this.props) => {
        const values = ['creators', 'likes', 'comments', 'tags', 'polls'];
        if (this.shouldRequestSaves(props)) values.push('saves');
        return values.join(',');
    }

    loadFeedPreference = async () => {
        if (!this.isPersonalizationEnabled() || this.props.personalized) return;
        const { articleStore, userStore } = this.props.stores;
        if (!userStore.user?.id) return;
        const preference = await loadFeedPreference({
            hostname: articleStore.currentHostname || window.location.hostname,
        });
        if (this.isUnmounted) return;
        this.setState({
            feedPreference: preference || emptyFeedPreference(),
            preferenceLoaded: Boolean(preference),
        });
    }

    handlePreferenceChange = (targetType, targetValue, followed, metadata = {}) => {
        const feedPreference = metadata.preference
            ? parseFeedPreference(metadata.preference)
            : updateFeedPreference(this.state.feedPreference, {
                targetType,
                targetValue,
                followed,
            });
        this.setState({ feedPreference, preferenceLoaded: true }, () => {
            if (this.props.personalized && metadata.confirmed && !followed) {
                this.setState({ offset: 0 }, () => this.loadAll(this.props, false, false));
            }
        });
    }

    preferenceReasonLabel = (article) => {
        const reasons = articlePreferenceReasons(article, this.state.feedPreference);
        return [reasons.author, ...reasons.topics.map(topic => `#${topic}`)].filter(Boolean).join(', ');
    }

    openStatsDrawer = (event, articleId) => {
        event.preventDefault();
        event.stopPropagation();
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;
        appState.openDrawer('articleStats', {
            height: drawerHeightLarge,
            articleId,
        }, drawerLevel + 1);
    }

    viewArticle = e => {
        const { view, showHeader, inputTitle, skipView } = this.props;
        if (skipView) {
            return;
        }
        if (view) {
            return view(e);
        }
        const { id, title } = e.target.closest('.box-line').dataset;
        if (parseInt(id, 10) < 0) {
            return;
        }
        const { appState, userStore } = this.props.stores;
        const {
            drawerLevel = 1,
            userOnly,
            showCreateButton,
            maxHeight,
            imageStyles,
         } = this.props;
        const { drawerHeightMediumLarge } = appState;
        const { isAdmin } = userStore;

        // console.log('viewArticle', { id });
        openArticle({
            stores: this.props.stores,
            id,
            articles: this.state.localList,
            drawerLevel,
            drawerProps: {
                height: drawerHeightMediumLarge,
                userOnly,
                maxHeight,
                imageStyles,
                editFunction: (isAdmin || userOnly || showCreateButton) ? this.editArticle : undefined,
            },
        });
    }

    editArticle = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id, title } = e.target.closest('button, .list-button').dataset;
        // console.log('editArticle', { id, title });
		const { drawerLevel = 1, userOnly, category, showCreateButton } = this.props;
		const { appState } = this.props.stores;
		const { drawerHeightMediumLarge } = appState;
        // console.log('editArticle', { id, title, userOnly, showCreateButton, category });
		appState.openDrawer('editArticle', {
			height: drawerHeightMediumLarge,
			id,
            userOnly: category === 'marketplace' || category === 'feed' ? 1 : 0,
            category,
            callback: () => {
                this.loadAll(this.props, false, false);
            },
		}, drawerLevel + 2);
	}

    markAsSold = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id, md5 } = e.target.closest('button, .list-button').dataset;
        const { articleStore } = this.props.stores;
        const { localList } = this.state;
        const currentArticle = localList.find(n => n.id === parseInt(id, 10));
        const value = !currentArticle.isSold;
        currentArticle.isSold = value;
        await articleStore.saveField(id, 'isSold', value);
    }

    deleteArticle = async e => {
        e.preventDefault();
        e.stopPropagation();
        const { id, md5 } = e.target.closest('button, .list-button').dataset;
        // console.log('deleteArticle', { id, md5 });
        const { articleStore } = this.props.stores;
        const { localList } = this.state;
        const currentArticle = localList.find(n => n.id === parseInt(id, 10));
        const value = currentArticle.active ? 0 : 1;
        currentArticle.active = value;
        await articleStore.saveField(id, 'active', value);
    }

    toggleAdminMenu = (e, articleId) => {
        e.stopPropagation();
        e.preventDefault();
        const { openMenuId } = this.state;
        this.setState({
            openMenuId: openMenuId === articleId ? null : articleId,
            showBlockConfirm: null,
            blockFeatures: ['articles'],
        });
    }

    closeAdminMenu = () => {
        this.setState({
            openMenuId: null,
            showBlockConfirm: null,
        });
    }

    handleOutsideClick = (e) => {
        const { openMenuId } = this.state;
        if (openMenuId !== null && !e.target.closest('.admin-article-menu')) {
            this.closeAdminMenu();
        }
    }

    toggleArticleOffline = async (e, articleId) => {
        e.stopPropagation();
        e.preventDefault();
        const { articleStore } = this.props.stores;
        const { localList } = this.state;
        const article = localList.find(a => a.id === articleId);
        if (article) {
            const newValue = article.offline ? 0 : 1;
            article.offline = newValue;
            await articleStore.saveField(articleId, 'offline', newValue);
            if (newValue === 1) {
                this.setState({
                    localList: localList.filter(a => a.id !== articleId),
                    openMenuId: null,
                });
            } else {
                this.setState({ openMenuId: null });
            }
        }
    }

    showBlockUserConfirm = (e, article) => {
        e.stopPropagation();
        e.preventDefault();
        this.setState({
            showBlockConfirm: article.id,
            blockUserId: article.user,
            blockUserName: article.creator || `User ${article.user}`,
            blockFeatures: ['articles'],
        });
    }

    toggleBlockFeature = (e, feature) => {
        e.stopPropagation();
        e.preventDefault();
        const { blockFeatures } = this.state;
        let newFeatures;
        if (feature === 'all') {
            newFeatures = blockFeatures.includes('all') ? [] : ['all'];
        } else {
            newFeatures = blockFeatures.filter(f => f !== 'all');
            const idx = newFeatures.indexOf(feature);
            if (idx >= 0) {
                newFeatures.splice(idx, 1);
            } else {
                newFeatures.push(feature);
            }
        }
        this.setState({ blockFeatures: newFeatures });
    }

    confirmBlockUser = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        const { blockedUserStore } = this.props.stores;
        const { blockUserId, blockFeatures } = this.state;
        await blockedUserStore.blockUser({
            user: blockUserId,
            blockedFeatures: blockFeatures,
            reason: 'Blocked by admin from feed',
        });
        this.setState({
            openMenuId: null,
            showBlockConfirm: null,
        });
    }

    cancelBlockUser = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.setState({
            showBlockConfirm: null,
        });
    }

    sendSmsToUser = (e, article) => {
        e.stopPropagation();
        e.preventDefault();
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightMediumLarge } = appState;

        const users = [];
        if (article.creatorCellphone) {
            const nameParts = (article.creator || '').split(' ');
            users.push({
                cellphone: article.creatorCellphone,
                firstname: nameParts[0] || '',
                lastname: nameParts.slice(1).join(' ') || '',
            });
        }

        this.setState({ openMenuId: null });

        appState.openDrawer('sendSms', {
            height: drawerHeightMediumLarge,
            users,
            from: 'Liverace',
            message: 'Hi! Please contact support at Liverace to get help: adm.liverace@femundlopet.no. Best regards Liverace :)',
        }, drawerLevel + 1);
    }

    onClickNewArticle = e => {
        const { drawerLevel = 1, userOnly, active = 1, showCreateButton } = this.props;
		const { appState } = this.props.stores;
		const { drawerHeightMediumLarge } = appState;
        const { category } = e.target.closest('button').dataset;
        // console.log('onClickNewArticle', { category, active, userOnly, showCreateButton });
		appState.openDrawer('newArticle', {
			height: drawerHeightMediumLarge,
            isNew: true,
            category,
            active,
            userOnly: userOnly || showCreateButton,
            callback: () => {
                this.loadAll(this.props, false, false);
                this.reloadOnceTimer = setTimeout(() => {
                    this.loadAll(this.props, false, false);
                }, 1000 * 2);
            },
		}, drawerLevel + 2);
	}

    retryVideoProcessing = async () => {
        const { articleStore } = this.props.stores;
        const { videoProcessingArticleId } = articleStore;
        if (!videoProcessingArticleId) return;

        await util.fetchApi(`/api/articles/${videoProcessingArticleId}`, { method: 'PATCH' }, {
            videoStatus: 'processing',
        });
        articleStore.pollVideoStatus(videoProcessingArticleId);
    }

    publishWithoutTranscript = async () => {
        const { articleStore } = this.props.stores;
        const { videoProcessingArticleId } = articleStore;
        if (!videoProcessingArticleId) return;

        await util.fetchApi(`/api/articles/${videoProcessingArticleId}`, { method: 'PATCH' }, {
            active: 1,
        });
        articleStore.updateKeyValue('videoProcessingStatus', null);
        articleStore.updateKeyValue('videoProcessingArticleId', null);
        this.loadAll(this.props, false, false);
    }

    onClickViewAllArticles = e => {
        const { drawerLevel = 1 } = this.props;
		const { appState, contactsStore } = this.props.stores;
		const { drawerHeightMediumLarge } = appState;
        const { category } = e.target.closest('button').dataset;

		appState.openDrawer('adminListArticle', {
			height: drawerHeightMediumLarge,
            skipUpdate: true,
            category,
            callback: () => {
                this.loadAll(this.props, false, false);
            },
		}, drawerLevel + 2);
	}

    toggleShowScheduled = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const showScheduled = !this.state.showScheduled;
        writeShowScheduled(showScheduled);
        this.setState({ showScheduled, offset: 0 }, () => {
            this.loadAll(this.props, false, false);
        });
    }

    toggleTagFilter = (tagOrEvent) => {
        // Handle both string (from filterBar) and event (from article tags)
        let tag;
        if (typeof tagOrEvent === 'string') {
            tag = tagOrEvent;
        } else {
            tagOrEvent.preventDefault();
            tagOrEvent.stopPropagation();
            tag = tagOrEvent.target.closest('span').dataset.tag;
        }
        const { filterByTags: filterByTagsProps } = this.props;
        if (filterByTagsProps?.length > 0) {
            return;
        }
        const { filterByTags } = this.state;
        const newFilterByTags = [...filterByTags];
        if (newFilterByTags.includes(tag)) {
            const index = newFilterByTags.indexOf(tag);
            newFilterByTags.splice(index, 1);
        } else {
            newFilterByTags.push(tag);
        }
        // Reset offset and reload from API with new filters
        this.setState({ filterByTags: newFilterByTags, offset: 0 }, () => {
            this.loadAll(this.props, false, false);
        });
    }


    filterByTags = (article) => {
        const { filterByTags, hideChildren, selectedAuthor } = this.state;
        const { filterByTags: filterByTagsProps } = this.props;
        if (hideChildren && article.parent) {
            return false;
        }

        // Filter by author if selected
        if (selectedAuthor && article.user !== selectedAuthor) {
            return false;
        }

        // Determine which tags to filter by
        const tagsToFilter = filterByTagsProps?.length > 0 ? filterByTagsProps : filterByTags;

        if (tagsToFilter.length === 0) {
            return true;
        }

        // Check both user tags and AI topic tags
        const hasMatchingUserTag = article.tags?.some(tag => tagsToFilter.includes(tag.name));
        const hasMatchingAiTag = article.aiTopicTags?.some(tag => tagsToFilter.includes(tag));

        return hasMatchingUserTag || hasMatchingAiTag;
    }

    onAuthorSelect = (userId) => {
        // Reset offset and reload from API with new author filter
        this.setState({ selectedAuthor: userId, offset: 0 }, () => {
            this.loadAll(this.props, false, false);
        });
    }

    onClearFilters = () => {
        // Reset offset and reload from API without filters
        this.setState({
            filterByTags: [],
            selectedAuthor: null,
            offset: 0,
        }, () => {
            this.loadAll(this.props, false, false);
        });
    }

    getAvailableTags = (articles) => {
        const tagsMap = new Map();

        articles.forEach(article => {
            // Collect user-defined tags only (AI tags are stored but not displayed)
            if (article.tags) {
                article.tags.forEach(tagObj => {
                    if (tagObj.name && !tagsMap.has(tagObj.name.trim())) {
                        tagsMap.set(tagObj.name.trim(), {
                            name: tagObj.name.trim(),
                            source: 'user',
                        });
                    }
                });
            }
        });

        return Array.from(tagsMap.values());
    }

    filter = () => {
        const { filterByTags } = this.state;
        if (filterByTags.length > 0) {
            return (<>
                {filterByTags.map(tag => {
                    return (<>
                        <span
                            class='badge text-bg-secondary me-1'
                            style='font-size: 1.0rem;'
                            onClick={this.toggleTagFilter}
                            data-tag={tag}
                        ><i class="fa-solid fa-circle-xmark"></i> {tag}</span>
                    </>);
                })}
            </>);
        }
    }

    viewTags = obj => {
        if (!obj) {
            return '';
        }
        if (obj.tags?.length === 0 && obj.aiTopicTags?.length === 0 && obj.loans?.length === 0 && !obj.ownedByOther && !obj.url && !obj.parents?.length > 0 && !obj.files?.length > 0 && !obj.isSold) {
            return '';
        }
        const { filterByTags } = this.state;

        const { showAuthor } = this.props;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const tagClass = darkmode ? 'dark' : 'light';

        return (<>
            <div class='w-100 overflow-auto'>
                <div class='box-line-tags d-flex flex-nowrap'>
                    {obj.ownedByOther && <>
                        <span class={`badge text-bg-danger me-1 mt-1`}>
                            <span class='font-weight-lighter'>Owner:</span> {obj.ownerFirstname} {obj.ownerLastname}
                        </span>
                    </>}
                    {obj.parents?.length > 0 && <>
                        <span class={`badge text-bg-light me-1 mt-1`}>
                            <i class='fa-duotone fa-code-branch' />
                        </span>
                    </>}
                    {/* {obj.url && <>
                        <span class={`badge text-bg-light me-1 mt-1`}>
                            <i class='fa-duotone fa-link' />
                        </span>
                    </>} */}
                    {/* {obj.files?.length > 0 && <>
                        <span class={`badge text-bg-light me-1 mt-1`}>
                            <i class='fa-duotone fa-file' /> <span class='font-weight-lighter'> x {obj.files.length}</span>
                        </span>
                    </>} */}
                    {obj.tags?.length > 0 && obj.tags.map(tag => {
                        let currentTagClass = tagClass;
                        if (filterByTags.includes(tag.name)) {
                            currentTagClass = 'primary';
                        }
                        return (
                            <span
                                key={`feed-topic-${obj.id}-${tag.name}`}
                                class='d-inline-flex align-items-center me-1 mt-1'
                            >
                                <span
                                    class={`badge text-bg-${currentTagClass}`}
                                    onClick={this.props.personalized ? undefined : this.toggleTagFilter}
                                    data-tag={tag.name}
                                    style={this.props.personalized ? '' : 'cursor: pointer;'}
                                >#{tag.name}</span>
                                {this.isPersonalizationEnabled()
                                    && this.state.preferenceLoaded
                                    && Boolean(tag.name)
                                    && this.isPersonalizableArticle(obj) && <FollowButton
                                    stores={this.props.stores}
                                    hostname={this.props.stores.articleStore.currentHostname || window.location.hostname}
                                    targetType='topic'
                                    targetValue={tag.name}
                                    followed={isTopicFollowed(this.state.feedPreference, tag.name)}
                                    surface={this.preferenceSurface()}
                                    onPreferenceChange={this.handlePreferenceChange}
                                />}
                            </span>
                        );
                    })}
                    {obj.loans?.length > 0 && obj.loans.map(loan => {
                        if (loan.endDate) {
                            return '';
                        }
                        const endDate = loan.endDate ? new Date(loan.endDate) : new Date();
                        const {
                            seconds,
                            minutes,
                            hours,
                            days,
                            weeks,
                            months,
                            years,
                        } = util.dateDiff(loan.startDate, endDate);
                        // console.log('viewTags', obj.startDate, { obj, seconds, minutes, hours, days, weeks, months, years })

                        return (<>
                            <span class={`badge text-bg-${loan.endDate ? 'success' : 'warning'} me-1 mt-1`}>
                                Loaned out <span class='font-weight-lighter'>({days > 0 ? <>{days}d</> : <>{hours}h</>})</span>
                            </span>

                            {/* <div class='tag'>{JSON.stringify(loan)}</div> */}
                        </>);
                    })}
                </div>
            </div>
        </>);
    }

    onClickMenu = e => {
        const { id } = e.target.closest('.list-menu').dataset;
        const idInt = parseInt(id, 10);
        const { articleStore } = this.props.stores;
        const { articles } = articleStore;
        const article = articles.find(n => n.id === idInt);

        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        const { drawerHeightSmall } = appState;
        // console.log('viewPart', { id });
        appState.openDrawer('articleMenu', {
            id,
            height: drawerHeightSmall,
            article,
            // callback: () => {
            //     appState.toggleDrawer();
            //     // this.loadAll();
            // },
        }, drawerLevel + 1);
    }

    buildListQuery = (props = this.props) => {
        const { articleId, category, userOnly, uuidv4, savedOnly, personalized, excludePortraitVideo } = props;
        const { articleStore } = this.props.stores;
        const { filterByTags, selectedAuthor, showScheduled } = this.state;
        const query = {
            hostname: articleStore.currentHostname,
        };
        if (category) {
            query.category = category;
        }
        if (excludePortraitVideo) {
            // Portrait phone clips live in the shorts swipe player, not the feed.
            query.excludePortraitVideo = 1;
        }
        if (showScheduled && category === 'feed') {
            query.includeScheduled = 1;
        }
        if (userOnly) {
            query.userOnly = true;
        }
        if (articleId) {
            query.articleId = articleId;
        }
        if (uuidv4) {
            query.uuidv4First = uuidv4;
        }
        if (savedOnly) {
            query.savedOnly = 1;
        }
        if (personalized) {
            query.personalized = 1;
        }
        if (filterByTags?.length > 0) {
            query.aiTopicTags = filterByTags;
        }
        if (selectedAuthor) {
            query.author = selectedAuthor;
        }
        return query;
    }

    fetchAndMergeNews = async (localList, { limit, offset, uuidv4 }) => {
        const articleItems = localList.map(item => ({ ...item, _feedType: 'article' }));
        const newsResponse = await util.fetchApi('/api/news/public/', { method: 'GET' }, {
            hasRace: 1,
            hasConvention: 1,
            limit,
            offset,
            sort: '-publishDate',
            addData: 'races,conventions,creators,likes,comments',
        });

        if (!(newsResponse?.status === 200 && newsResponse.data)) {
            this.lastNewsFetchFailed = true;
            this.lastNewsResponseFromCache = false;
            return articleItems;
        }
        this.lastNewsFetchFailed = false;
        this.lastNewsResponseFromCache = Boolean(newsResponse._fromCache);
        const newsItems = (newsResponse.data || []).map(item => ({
            ...item,
            _feedType: 'news',
            _likeType: 'news',
        }));
        // Store races and conventions for byline display
        const newsRaces = {};
        if (newsResponse.included?.races) {
            newsResponse.included.races.forEach(r => { newsRaces[r.id] = r; });
        }
        const newsConventions = {};
        if (newsResponse.included?.conventions) {
            newsResponse.included.conventions.forEach(c => { newsConventions[c.id] = c; });
        }
        this.setState({ newsRaces, newsConventions });
        // Merge and sort by publishDate descending
        return [...articleItems, ...newsItems].sort((a, b) => {
            // Keep uuidv4First article pinned at the top
            if (uuidv4) {
                if (a.uuidv4 === uuidv4) return -1;
                if (b.uuidv4 === uuidv4) return 1;
            }
            const dateA = new Date(a.publishDate || a.createdDate);
            const dateB = new Date(b.publishDate || b.createdDate);
            return dateB - dateA;
        });
    }

    newestItemForSource = (list, source) => {
        return list
            .filter(item => (source === 'news') === (item._feedType === 'news'))
            .reduce((newest, item) => (
                compareCursorKeys(itemCursorKey(item), itemCursorKey(newest)) > 0 ? item : newest
            ), null);
    }

    prepareInitialReturnList = async (list) => {
        if (!this.isReturnFoundationEnabled() || !this.isCanonicalFeed() || this.feedStateInitialized) {
            return { list, count: 0, capped: false };
        }
        this.feedStateInitialized = true;
        let state = await this.feedStatePromise;
        const newestArticle = this.newestItemForSource(list, 'article');
        const newestNews = this.newestItemForSource(list, 'news');
        const needsArticleBaseline = newestArticle && !state?.articleCursorKey;
        const needsNewsBaseline = newestNews && !state?.newsCursorKey;

        if (!state || needsArticleBaseline || needsNewsBaseline) {
            const baseline = {
                articleId: needsArticleBaseline || !state ? newestArticle?.id : null,
                newsId: needsNewsBaseline || !state ? newestNews?.id : null,
                articleCursorKey: needsArticleBaseline || !state ? itemCursorKey(newestArticle) : state?.articleCursorKey,
                newsCursorKey: needsNewsBaseline || !state ? itemCursorKey(newestNews) : state?.newsCursorKey,
            };
            state = await this.feedStateClient.acknowledge(baseline);
            this.feedAcknowledgement = state || baseline;
            const summary = summarizeNewItems(
                list,
                this.feedAcknowledgement,
                this.props.limit || this.state.limit,
            );
            return {
                list: markNewItems(list, this.feedAcknowledgement),
                ...summary,
            };
        }

        this.feedAcknowledgement = state;
        const summary = summarizeNewItems(list, state, this.props.limit || this.state.limit);
        return {
            list: markNewItems(list, state),
            ...summary,
        };
    }

    emitNewPostsShown = (summary = {}) => {
        if (!this.feedTelemetry || summary.count < 1) return;
        const signature = [
            summary.count,
            summary.newestArticleCursorKey || '',
            summary.newestNewsCursorKey || '',
        ].join(':');
        if (signature === this.lastShownPillSignature) return;
        this.lastShownPillSignature = signature;
        this.feedTelemetry.emit('feed_new_posts_shown', {
            count: summary.count,
            capped: Boolean(summary.capped),
        });
    }

    setupFeedRuntime = () => {
        const { appState, articleStore, userStore } = this.props.stores;
        const hostname = articleStore.currentHostname || window.location.hostname;
        if (this.isReturnFoundationEnabled() && this.isCanonicalFeed() && !this.feedStateClient) {
            this.feedStateClient = createFeedStateClient({
                hostname,
                userId: userStore.user?.id || null,
            });
            this.feedStatePromise = this.feedStateClient.load();
            if (this.lastSourcePageComplete && this.state.localList.length > 0) {
                this.prepareInitialReturnList(this.state.localList).then(initialState => {
                    if (this.isUnmounted) return;
                    this.setState({
                        localList: initialState.list,
                        pendingNewCount: initialState.count,
                        pendingNewCapped: initialState.capped,
                    });
                    this.emitNewPostsShown(initialState);
                    this.acknowledgeAppliedList(initialState.list);
                });
            }
        }

        const telemetrySurface = this.props.personalized
            ? 'personalized'
            : (this.props.savedOnly ? 'saved' : (this.isCanonicalFeed() ? 'feed' : null));
        if (telemetrySurface && userStore.user?.id && !this.feedTelemetry) {
            this.feedTelemetry = new FeedTelemetry({
                appState,
                surface: telemetrySurface,
                root: appState.appContainer || null,
            });
            if (['feed', 'personalized'].includes(telemetrySurface)) {
                this.feedTelemetry.startVisit({ source: 'mount', offline: !navigator.onLine });
            }
        }
    }

    observeFeedElement = (item, element) => {
        if (Number(item?.id) > 0) {
            this.feedTelemetry?.observeElement(item, element);
        }
    }

    handleSavedChange = (articleId, saved, metadata = {}) => {
        const id = Number(articleId);
        const savedArticleIds = new Set(this.state.savedArticleIds);
        if (saved) {
            savedArticleIds.add(id);
        } else {
            savedArticleIds.delete(id);
        }

        let localList = this.state.localList;
        if (this.props.savedOnly && metadata.optimistic && !saved) {
            const index = localList.findIndex(item => item.id === id);
            if (index >= 0) {
                this.removedSavedItems.set(id, { item: localList[index], index });
                localList = localList.filter(item => item.id !== id);
            }
        } else if (this.props.savedOnly && metadata.rollback && saved) {
            const removed = this.removedSavedItems.get(id);
            if (removed && !localList.some(item => item.id === id)) {
                localList = [...localList];
                localList.splice(Math.min(removed.index, localList.length), 0, removed.item);
                this.removedSavedItems.delete(id);
            }
        } else if (this.props.savedOnly && metadata.confirmed && !saved) {
            this.removedSavedItems.delete(id);
        }

        this.setState({
            savedArticleIds,
            localList,
            totalArticles: localList.length,
        });
    }

    applyPendingNewPosts = (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        const { pendingRefreshList, localList, pendingNewCount, pendingNewCapped } = this.state;
        const applied = mergePendingPage(pendingRefreshList || localList, localList);
        const summary = summarizeNewItems(
            applied,
            this.feedAcknowledgement || {},
            this.props.limit || this.state.limit,
        );
        const nextAcknowledgement = {
            ...(this.feedAcknowledgement || {}),
            articleCursorKey: summary.newestArticleCursorKey
                || this.feedAcknowledgement?.articleCursorKey
                || null,
            newsCursorKey: summary.newestNewsCursorKey
                || this.feedAcknowledgement?.newsCursorKey
                || null,
        };
        this.feedAcknowledgement = nextAcknowledgement;
        this.setState({
            localList: applied.map(item => ({ ...item, _isNew: false })),
            totalArticles: applied.length,
            offset: applied.length,
            pendingRefreshList: null,
            pendingNewCount: 0,
            pendingNewCapped: false,
        });
        const { appState } = this.props.stores;
        if (appState.appContainer) {
            appState.appContainer.scrollTop = 0;
        }
        this.feedStateClient?.acknowledge({
            articleId: summary.newestArticleId,
            newsId: summary.newestNewsId,
            articleCursorKey: summary.newestArticleCursorKey,
            newsCursorKey: summary.newestNewsCursorKey,
        }).then(state => {
            if (state) this.feedAcknowledgement = state;
        });
        this.feedTelemetry?.emit('feed_new_posts_opened', {
            count: pendingNewCount,
            capped: Boolean(pendingNewCapped),
        });
        this.lastShownPillSignature = null;
    }

    // A fresh non-append page renders from the top, so the member has seen its
    // newest rows. Advance the acknowledgement — otherwise the next visit or
    // return-refresh counts the same rows as new again and the new-posts pill
    // lags one refresh behind what is actually unseen. Rows withheld in
    // pendingRefreshList are never passed here; they stay unacknowledged until
    // the pill is opened.
    acknowledgeAppliedList = (list = []) => {
        if (!this.feedStateClient || !this.isReturnFoundationEnabled() || !this.isCanonicalFeed()) {
            return;
        }
        const newestArticle = this.newestItemForSource(list, 'article');
        const newestNews = this.newestItemForSource(list, 'news');
        const articleCursorKey = itemCursorKey(newestArticle);
        const newsCursorKey = itemCursorKey(newestNews);
        const articleAhead = Boolean(articleCursorKey)
            && compareCursorKeys(articleCursorKey, this.feedAcknowledgement?.articleCursorKey) > 0;
        const newsAhead = Boolean(newsCursorKey)
            && compareCursorKeys(newsCursorKey, this.feedAcknowledgement?.newsCursorKey) > 0;
        if (!articleAhead && !newsAhead) {
            return;
        }
        this.feedStateClient.acknowledge({
            articleId: articleAhead ? newestArticle?.id : null,
            newsId: newsAhead ? newestNews?.id : null,
            articleCursorKey: articleAhead ? articleCursorKey : null,
            newsCursorKey: newsAhead ? newsCursorKey : null,
        }).then(state => {
            if (state) {
                this.feedAcknowledgement = state;
            }
        });
    }

    renderNewPostsPill = () => {
        const { pendingNewCount, pendingNewCapped } = this.state;
        if (!this.isReturnFoundationEnabled() || !this.isCanonicalFeed() || pendingNewCount < 1) {
            return null;
        }
        const id = pendingNewCapped
            ? 'article.new-posts-capped'
            : pendingNewCount === 1 ? 'article.new-post' : 'article.new-posts';
        return (
            <div class='sticky-top d-flex justify-content-center mb-2' style='z-index: 20; top: 0.5rem;'>
                <button
                    type='button'
                    class='btn btn-primary rounded-pill shadow-sm'
                    onClick={this.applyPendingNewPosts}
                >
                    <i class='fa-solid fa-arrow-up me-2' />
                    <Text id={id} fields={{ count: pendingNewCount }}>
                        {pendingNewCount === 1 ? '1 new post' : `${pendingNewCount}${pendingNewCapped ? '+' : ''} new posts`}
                    </Text>
                </button>
            </div>
        );
    }

    // ----- Refresh on return (spec: docs/superpowers/specs/2026-08-06-feed-refresh-on-return-design.md)
    // Both event pairs are needed: tab switches fire visibilitychange, while
    // switching to another application window may only fire window blur/focus.

    handleVisibilityChange = () => {
        if (document.visibilityState === 'hidden') {
            this.markHidden();
        } else {
            this.handleReturnToPage();
        }
    }

    handleWindowBlur = () => {
        this.markHidden();
    }

    handleWindowFocus = () => {
        this.handleReturnToPage();
    }

    markHidden = () => {
        // Keep the earliest stamp: blur followed by tab-hide must not reset the clock.
        if (!this.hiddenAt) {
            this.hiddenAt = Date.now();
        }
    }

    handleReturnToPage = async () => {
        const { hiddenAt } = this;
        // Clear immediately so the second event of a visible+focus pair sees no stamp.
        this.hiddenAt = null;
        if (!shouldRefreshOnReturn({
            hiddenAt,
            lastRefreshAt: this.lastReturnRefreshAt,
            now: Date.now(),
        })) {
            return;
        }
        // After sleep/wake the JWT may be mid-renewal; wait so the fetch
        // carries a fresh token. Resolving false is fine — the public feed
        // endpoint works unauthenticated.
        const pendingRefresh = getVisibilityRefreshPromise();
        if (pendingRefresh) {
            await pendingRefresh;
        }
        this.lastReturnRefreshAt = Date.now();
        this.refreshInPlace();
    }

    refreshInPlace = async () => {
        if (this.returnRefreshInProgress) {
            return;
        }
        this.returnRefreshInProgress = true;
        try {
            const props = this.props;
            const { limit: propsLimit, uuidv4, includeRaceNews } = props;
            const { articleStore, raceStore, userStore, appState } = props.stores;
            const { isAdmin } = userStore;
            const { isRaceAdmin, isMediaWorker } = raceStore;
            const isWorkingWithMedia = isAdmin || isRaceAdmin || isMediaWorker;
            this.feedTelemetry?.startVisit({ source: 'return', offline: !navigator.onLine });

            // Snapshot the list length so we can detect a concurrent
            // loadMore()/loadAll() landing while this refresh is in flight.
            const startListLength = this.state.localList.length;

            // Re-fetch everything currently on screen in one page so the swap
            // preserves scroll position; new posts land at the top.
            const fetchLimit = Math.max(this.state.localList.length, propsLimit || this.state.limit);
            const query = this.buildListQuery(props);
            const apiUrl = isWorkingWithMedia ? '/api/articles/' : '/api/articles/public/';
            const response = await util.fetchApi(apiUrl, { method: 'GET' }, {
                ...query,
                limit: fetchLimit,
                offset: 0,
                sort: '-sort -createdDate',
                addData: this.articleAddData(props),
            });
            if (response?.status !== 200) {
                return;
            }
            if (response._fromCache) {
                // Never replace a live-rendered list with cached data — a
                // cached page can be shorter than what is already on screen.
                return;
            }
            let newList = response.data || [];
            if (response.included?.creators) {
                articleStore.updateKeyValue('creators', response.included.creators);
            }
            if (response.included?.tags) {
                articleStore.updateKeyValue('tags', response.included.tags);
            }
            if (this.shouldRequestSaves(props)) {
                this.setState({
                    savedArticleIds: new Set(response.included?.savedArticleIds || []),
                });
            }
            if (includeRaceNews) {
                newList = await this.fetchAndMergeNews(newList, {
                    limit: fetchLimit,
                    offset: 0,
                    uuidv4,
                });
                if (this.lastNewsFetchFailed || this.lastNewsResponseFromCache) {
                    return;
                }
            }
            if (newList.length === 0) {
                // Never blank the feed from a background refresh.
                return;
            }
            if (this.state.localList.length !== startListLength) {
                // A loadMore()/loadAll() appended or replaced items while this
                // refresh was in flight - applying newList now would silently
                // drop them. Skip this round; the next return-to-page retries.
                return;
            }

            if (this.isReturnFoundationEnabled() && this.isCanonicalFeed(props)) {
                const current = this.state.localList;
                const existingPending = this.state.pendingRefreshList;
                const comparisonList = existingPending || current;
                const knownKeys = new Set(comparisonList.map(feedItemKey));
                const identityNewRows = newList.filter(item => !knownKeys.has(feedItemKey(item)));

                if (existingPending || identityNewRows.length > 0) {
                    const pendingRefreshList = mergePendingPage(newList, comparisonList);
                    const summary = summarizeNewItems(
                        pendingRefreshList,
                        this.feedAcknowledgement || {},
                        propsLimit || this.state.limit,
                    );
                    if (summary.count > 0) {
                        const newPageSummary = summarizeNewItems(
                            newList,
                            this.feedAcknowledgement || {},
                            propsLimit || this.state.limit,
                        );
                        const displaySummary = { ...summary, capped: newPageSummary.capped };
                        const refreshedCurrent = mergeEngagementWithoutNewRows(current, newList);
                        this.setState({
                            localList: refreshedCurrent,
                            pendingRefreshList,
                            pendingNewCount: summary.count,
                            pendingNewCapped: displaySummary.capped,
                            offlineData: false,
                        });
                        this.emitNewPostsShown(displaySummary);
                        return;
                    }
                }

                const container = appState.appContainer;
                const previousScrollTop = container ? container.scrollTop : 0;
                const previousScrollHeight = container ? container.scrollHeight : 0;
                this.setState({
                    localList: newList,
                    totalArticles: newList.length,
                    offset: newList.length,
                    pendingRefreshList: null,
                    pendingNewCount: 0,
                    pendingNewCapped: false,
                    offlineData: false,
                }, () => {
                    if (container && previousScrollTop > 0) {
                        const delta = container.scrollHeight - previousScrollHeight;
                        if (delta > 0) container.scrollTop = previousScrollTop + delta;
                    }
                });
                return;
            }

            // Anchor the viewport: new posts prepend above the fold and would
            // otherwise push the content the user is reading downwards.
            const container = appState.appContainer;
            const prevScrollTop = container ? container.scrollTop : 0;
            const prevScrollHeight = container ? container.scrollHeight : 0;

            this.setState({
                localList: newList,
                totalArticles: newList.length,
                offset: newList.length,
                offlineData: false,
            }, () => {
                if (container && prevScrollTop > 0) {
                    const delta = container.scrollHeight - prevScrollHeight;
                    if (delta > 0) {
                        container.scrollTop = prevScrollTop + delta;
                    }
                }
            });
        } finally {
            this.returnRefreshInProgress = false;
        }
    }

    // ----- Pending (offline-queued) posts (spec: docs/superpowers/specs/2026-08-09-offline-post-creation-design.md)

    loadPendingPosts = async () => {
        const { category, savedOnly } = this.props;
        if (savedOnly) {
            this.setState({ pendingPosts: [] });
            return;
        }
        let entries = [];
        try { entries = await offlineWriteQueue.getAll(); } catch (e) { entries = []; }
        const pendingPosts = entries
            .filter(entry => entry && entry.type === 'article')
            .filter(entry => !category || (entry.saleData && entry.saleData.category) === category)
            .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
            .map(entry => ({
                ...entry.saleData,
                id: entry.tempId,
                _feedType: 'article',
                _pendingSync: entry.status === 'pending',
                _syncFailed: entry.status === 'failed',
                _syncError: entry.lastError || null,
            }));
        this.setState({ pendingPosts });
    }

    onPendingPostsChanged = (msg, data) => {
        this.loadPendingPosts();
        if (data && data.realId) {
            // The queued post just became a real article — refresh the list
            // so the pending card is replaced by the server row.
            this.loadAll(this.props, false, false);
        }
    }

    retryPendingPost = async (e) => {
        e.stopPropagation();
        const tempId = parseInt(e.target.closest('button').dataset.id, 10);
        await offlineWriteQueue.markRetry(tempId);
        this.loadPendingPosts();
        drainQueues().catch(() => {});
    }

    discardPendingPost = async (e) => {
        e.stopPropagation();
        const tempId = parseInt(e.target.closest('button').dataset.id, 10);
        let entries = [];
        try { entries = await offlineWriteQueue.getAll(); } catch (err) { entries = []; }
        const entry = entries.find(en => en && en.tempId === tempId);
        const keys = ((entry && entry.saleData && entry.saleData.images) || [])
            .filter(img => img && img._pendingUpload && img.pendingImageKey)
            .map(img => img.pendingImageKey);
        await offlineWriteQueue.discard(tempId);
        await discardImages(keys);
        this.loadPendingPosts();
    }

    renderPendingBadge = (obj) => {
        if (!obj || (!obj._pendingSync && !obj._syncFailed)) return null;
        if (obj._syncFailed) {
            return (
                <div class='alert alert-danger py-1 px-2 small d-flex align-items-center justify-content-between w-100 mb-0'>
                    <span><i class='fa-solid fa-triangle-exclamation me-2' /><Text id='article.pending-failed'>Could not publish</Text></span>
                    <span class='text-nowrap'>
                        <button type='button' class='btn btn-sm btn-outline-danger rounded-pill me-2' onClick={this.retryPendingPost} data-id={obj.id}>
                            <Text id='article.pending-retry'>Try again</Text>
                        </button>
                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.discardPendingPost} data-id={obj.id}>
                            <Text id='article.pending-discard'>Discard</Text>
                        </button>
                    </span>
                </div>
            );
        }
        return (
            <div class='alert alert-warning py-1 px-2 small w-100 mb-0'>
                <i class='fa-solid fa-cloud-arrow-up me-2' />
                <Text id='article.pending-sync'>Will be published when you are online</Text>
            </div>
        );
    }

    renderOfflineBanner = () => {
        if (!this.state.offlineData) {
            return null;
        }
        return (
            <div class='alert alert-warning py-1 px-2 small text-center mb-2'>
                <i class='fa-solid fa-cloud-slash me-2' />
                <Text id='article.offline-showing-saved'>You are offline – showing saved content</Text>
            </div>
        );
    }

    loadAll = async (props = this.props, append, emptyFirst = false) => {
        const { limit, totalArticles } = this.state;
        let { offset } = this.state;
        if (!append) {
            offset = 0;
            this.setState({ offset: 0 });
        }
        const { raceId, tag, skipUpdate, limit: propsLimit, uuidv4 } = props;
        const { articleStore, raceStore, userStore } = this.props.stores;
        const { isAdmin } = userStore;
        const { isRaceAdmin, isMediaWorker } = raceStore;
        const isWorkingWithMedia = isAdmin || isRaceAdmin || isMediaWorker;

        if (emptyFirst && !skipUpdate) {
            articleStore.updateKeyValue('articles', {});
        }

        clearTimeout(this.reloadTimer);
        const query = this.buildListQuery(props);

        // Make direct API call to capture full response including included.creators
        const apiUrl = isWorkingWithMedia ? '/api/articles/' : '/api/articles/public/';
        const response = await util.fetchApi(apiUrl, { method: 'GET' }, {
            ...query,
            limit: propsLimit || limit,
            offset,
            sort: props.personalized ? '-publishDate -id' : '-sort -createdDate',
            addData: this.articleAddData(props),
        });

        const fromCache = !!(response && response._fromCache);
        const netError = isNetworkErrorEnvelope(response);

        // Offline: a failed refresh must never blank an already-rendered feed.
        if (!append && netError && this.state.localList.length > 0) {
            this.setState({ offlineData: true, loading: false });
            if (!skipUpdate && offset === 0) {
                this.reloadTimer = setTimeout(() => {
                    this.loadAll(props, false, false);
                }, 1000 * 60);
            }
            return this.state.localList;
        }

        let localList = [];
        if (response?.status === 200) {
            localList = response.data || [];

            // Store creators from included data
            if (response.included?.creators) {
                articleStore.updateKeyValue('creators', response.included.creators);
            }

            // Store tags from included data
            if (response.included?.tags) {
                articleStore.updateKeyValue('tags', response.included.tags);
            }

            if (response.included?.feedPreference) {
                this.setState({
                    feedPreference: parseFeedPreference(response.included.feedPreference),
                    preferenceLoaded: true,
                });
            }

            if (props.personalized && !append && !this.personalizedLoadEmitted) {
                const preference = response.included?.feedPreference
                    ? parseFeedPreference(response.included.feedPreference)
                    : this.state.feedPreference;
                const matches = localList.map(article => articlePreferenceReasons(article, preference));
                const authorMatches = matches.filter(match => Boolean(match.author)).length;
                const topicMatches = matches.filter(match => match.topics.length > 0).length;
                const bothMatches = matches.filter(match => Boolean(match.author) && match.topics.length > 0).length;
                this.feedTelemetry?.emit('feed_personalized_loaded', {
                    itemCount: localList.length,
                    authorMatches,
                    topicMatches,
                    bothMatches,
                });
                this.personalizedLoadEmitted = true;
            }

            if (this.shouldRequestSaves(props)) {
                const nextSavedIds = new Set(append ? this.state.savedArticleIds : []);
                (response.included?.savedArticleIds || []).forEach(id => nextSavedIds.add(Number(id)));
                this.setState({ savedArticleIds: nextSavedIds });
            }

            // Update store articles if not skipping update
            if (!skipUpdate) {
                if (append) {
                    const existingArticles = articleStore.articles || [];
                    articleStore.updateKeyValue('articles', [...existingArticles, ...localList]);
                } else {
                    articleStore.updateKeyValue('articles', localList);
                }
            }
        }

        // Fetch and merge race news if includeRaceNews is enabled
        const { includeRaceNews } = props;
        if (includeRaceNews) {
            localList = await this.fetchAndMergeNews(localList, {
                limit: propsLimit || limit,
                offset,
                uuidv4,
            });
        }

        let initialReturnState = { list: localList, count: 0, capped: false };
        const sourcePageComplete = response?.status === 200
            && (!includeRaceNews || !this.lastNewsFetchFailed);
        this.lastSourcePageComplete = sourcePageComplete;
        if (!append && localList.length > 0 && sourcePageComplete) {
            initialReturnState = await this.prepareInitialReturnList(localList);
            localList = initialReturnState.list;
        }

        // Update local state
        let newLocalList = localList;
        if (append) {
            // When appending, always keep existing items and add new ones
            newLocalList = mergePendingPage(this.state.localList, localList);
        }
        // Only update state if we have items or it's not an append operation
        if (!append || localList.length > 0) {
            this.setState({
                localList: newLocalList,
                totalArticles: append ? totalArticles + localList.length : localList.length,
                offlineData: fromCache || netError,
                loading: false,
                ...(!append ? {
                    pendingNewCount: initialReturnState.count,
                    pendingNewCapped: initialReturnState.capped,
                    pendingRefreshList: null,
                } : {}),
                ...(append && this.state.pendingRefreshList ? {
                    pendingRefreshList: mergePendingPage(this.state.pendingRefreshList, localList),
                } : {}),
            });
        }
        if (!append) {
            this.emitNewPostsShown(initialReturnState);
            if (sourcePageComplete && !fromCache && !netError && localList.length > 0) {
                this.acknowledgeAppliedList(localList);
            }
        }

        if (!skipUpdate && !append && offset === 0) {
            this.reloadTimer = setTimeout(() => {
                this.loadAll(props, false, false);
            }, 1000 * 60);
        } else {
            clearTimeout(this.reloadTimer);
        }
        return newLocalList;
    }

    loadMore = async () => {
        const { limit, offset, totalArticles } = this.state;
        let newOffset = offset + limit;
        if (newOffset >= totalArticles) {
            newOffset = totalArticles;
        }
        // console.log('loadMore', { limit, offset, totalArticles, newOffset });
        this.setState({
            offset: newOffset,
        }, () => {
            this.loadAll(this.props, true, false);
        });
    }

    setLoadMore = () => {
        const { skipUpdate } = this.props;
        const { articleStore } = this.props.stores;
        if (skipUpdate) {
            articleStore.addRefreshPageFunction(() => {
                this.setState({ offset: 0 }, () => {
                    this.loadAll(this.props, false, false);
                });
            }, this.props.category);

            articleStore.addLoadMoreFunction(() => {
                this.loadMore();
            }, this.props.category);

            return false;
        }
        articleStore.setLoadMore(() => {
            // console.log('load more');
            clearTimeout(this.reloadTimer);
            let { limit, offset } = this.state;
            offset += limit;
            this.setState({ offset }, () => {
                this.loadAll(this.props, true, false);
            });
        });

        articleStore.setRefreshPage(() => {
            clearTimeout(this.reloadTimer);
            let { limit, offset } = this.state;
            offset = 0;
            this.setState({ offset }, () => {
                this.loadAll(this.props, false, false);
            });
        });
    }

    unsetLoadMore = () => {
        const { skipUpdate } = this.props;
        const { articleStore } = this.props.stores;
        if (skipUpdate) {
            articleStore.removeLoadMoreFunction(this.props.category);
            articleStore.removeRefreshPageFunction(this.props.category);
            return false;
        }
        articleStore.setLoadMore(() => {});
        articleStore.setRefreshPage(() => {});
    }

    componentDidMount() {
        this.isUnmounted = false;
        this.setupFeedRuntime();
        this.loadFeedPreference();
        this.loadAll();
        this.setLoadMore();
        document.addEventListener('click', this.handleOutsideClick);
        if (this.props.refreshOnReturn) {
            document.addEventListener('visibilitychange', this.handleVisibilityChange);
            window.addEventListener('focus', this.handleWindowFocus);
            window.addEventListener('blur', this.handleWindowBlur);
        }
        this.loadPendingPosts();
        this.pendingPostsToken = PubSubJS.subscribe('articlePendingChanged', this.onPendingPostsChanged);
    }

    componentWillUnmount() {
        this.isUnmounted = true;
        clearTimeout(this.reloadTimer);
        this.unsetLoadMore();
        document.removeEventListener('click', this.handleOutsideClick);
        if (this.props.refreshOnReturn) {
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
            window.removeEventListener('focus', this.handleWindowFocus);
            window.removeEventListener('blur', this.handleWindowBlur);
        }
        if (this.pendingPostsToken) {
            PubSubJS.unsubscribe(this.pendingPostsToken);
        }
        this.feedTelemetry?.disconnect();
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.category !== this.props.category || nextProps.offset !== this.props.offset || nextProps.uuidv4 !== this.props.uuidv4) {
            if (nextProps.offset) {
                this.setState({ offset: nextProps.offset });
            }
            this.loadAll(nextProps);
        }
    }

    componentDidUpdate() {
        this.setupFeedRuntime();
    }

    // Segment renderer for feed article ingress/body text: plain markdown when
    // there are no inline widget markers ({{poll}}, {{results.N}}, …), otherwise
    // one element per segment so widgets render inline with the surrounding
    // text. feed-post-text marks the post's own text (not widgets) so
    // background-post text patterns can clip to it — see
    // .background-text-pattern in custom.css. Shared by elDescription (card
    // body) and renderBackgroundIngress.
    renderFeedSegments = (article, text, wrapClass) => {
        if (!text) return null;
        const linkedText = linkifyMentions(text, article.mentions);
        const segments = splitBodySegments(linkedText);
        const hasMarkers = segments.length > 1 || segments[0]?.type !== 'markdown';
        if (!hasMarkers) {
            return <div class={`feed-post-text ${wrapClass || 'font-weight-normal mt-3 mb-2'}`}><Markdown markdown={linkedText} markedOpts={MARKDOWN_OPTIONS} /></div>;
        }
        return segments.map((segment, idx) => {
            if (segment.type === 'markdown') {
                return <div key={`seg-${idx}`} class='font-weight-normal feed-post-text'><Markdown markdown={segment.content} markedOpts={MARKDOWN_OPTIONS} /></div>;
            } else if (segment.type === 'results') {
                const rl = article.resultsLists?.[segment.resultsIndex];
                if (rl) return <ResultsList key={`results-${idx}`} resultsList={rl} stores={this.props.stores} />;
            } else if (segment.type === 'factbox') {
                return <FactBox key={`fb-${segment.articleId}`} articleId={segment.articleId} stores={this.props.stores} />;
            } else if (segment.type === 'lesogsa') {
                return <ReadAlsoBox key={`la-${segment.articleId}`} articleId={segment.articleId} stores={this.props.stores} />;
            } else if (segment.type === 'product') {
                return <ProductBox key={`prod-${segment.productId}`} productId={segment.productId} stores={this.props.stores} />;
            } else if (segment.type === 'poll' && article.polls?.[0]) {
                return <Poll key={`poll-${idx}`} poll={article.polls[0]} stores={this.props.stores} />;
            }
            return null;
        });
    }

    // Ingress renderer for background/story cards: per-line sized divs when the
    // text carries size markers or emoji-only lines, otherwise the segment
    // renderer with the '-# ' markers stripped. Shared by elDescription (card
    // body) and elOverlayText (text drawn over the photo in story mode).
    renderBackgroundIngress = (e) => {
        const perLine = (isBackgroundPost(e) || isStoryPost(e)) && usesPerLineSizes(e.ingress);
        if (perLine) {
            return (
                <div class='w-100 mt-2 bg-post-lines feed-post-text'>
                    <style>{'.bg-post-lines p { margin-bottom: 0.15em; }'}</style>
                    {parseBackgroundLines(linkifyMentions(e.ingress, e.mentions)).map((line, idx) => (
                        <div key={`bgline-${idx}`} style={`font-size: ${lineFontSize(line)};`}>
                            {line.text ? <Markdown markdown={line.text} markedOpts={MARKDOWN_OPTIONS} /> : <br />}
                        </div>
                    ))}
                </div>
            );
        }
        return this.renderFeedSegments(e, stripSizeMarkers(e.ingress), 'mt-2');
    }

    render() {
        const { appState, userStore, articleStore, presenceStore } = this.props.stores;
        const { isDevelopment, path, apiServer, currentCustomer = {} } = appState;
        const { user, isAdmin, isBlockedFromArticles } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const isLoggedIn = user && user.id && user.id > 0;

        const {
            articleContainerClasses  = [],
            articleListClasses  = [],
            articleListImageWidth  = [],
            articleListImageLocations  = [],
        } = currentCustomer || {};

        const { articles } = articleStore;
        const { titleText, showHeader, inputTitle, skipUpdate, category } = this.props;
        const {
            classNames = '',
            hideEmpty,
            title = titleText,
            editElement,
            help,
            children,
            largeList,
            frontpageList,
            tickerList,
            articles: list,
            raceId,
            tag,
            label,
            labelBackgroundClass = 'bg-dark',
            labelClass = 'text-white',
            buttonClass = 'btn-success',
            buttonTextClass = 'text-white',
            userOnly,
            fullWidthOnly,
            elTitle,
            showAuthor,
            showTitle = true,
            maxHeight = '60vh',
            tickerMaxHeight = '60px',
            tickerMinHeight = '60px',
            tickerImgHeight = '60px',
            tickerImgRatio,
            width,
            imageStyles, // = 'aspect-ratio: 4 / 3',
            showBylineHeader,
            showComments,
            showLikes,
            showCreateButton,
            createButtonText,
            showSoMeStyle,
            elInfo1 = e => util.formatDistance(e.publishDate, new Date(), { textAgo: '' }),
            elInfo2 = e => '',
            elInfo3 = e => '',
            buttonTop = '10',
            buttonRight = '10',
            checkContentHeight,
            showFiles,
            hideAdminButtons,
            cutoffMonths,
            savedOnly,
            personalized,
        } = this.props;

        const { localList, totalArticles } = this.state;
        let finalList = skipUpdate ? localList : list || articles;
        finalList = finalList.filter(this.filterByTags);
        if (cutoffMonths > 0) {
            const cutoffDate = new Date();
            cutoffDate.setMonth(cutoffDate.getMonth() - cutoffMonths);
            finalList = finalList.filter(e => {
                const d = new Date(e.publishDate || e.createdDate);
                return d >= cutoffDate;
            });
        }


        if (tickerList) {
            return (<>
                <div class='d-flex flex-column w-100 position-relative'>
                    {label && finalList?.length > 0 && <>
                        <div
                            class={`${labelBackgroundClass} rounded position-absolute`}
                            style={`
                                bottom: 0;
                                left: 0;
                                width: 1.2rem !important;
                                min-width: 1.2rem !important;
                                height: calc(100% - 0.25rem) !important;
                                border-radius: 0.25rem 0 0 0.25rem !important;
                            `}
                        >
                            <div
                                class={`position-absolute ${labelClass} px-2`}
                                style={`
                                    bottom: -0.6rem;
                                    left: 0.3rem;
                                    font-size: 0.6rem;
                                    line-height: 0.6rem;
                                    transform: rotate(-90deg);
                                    transform-origin: top left;
                                    text-transform: uppercase;
                                `}>
                                {label}
                            </div>
                            &nbsp;
                        </div>
                    </>}
                    {isAdmin ? <>
                        <div class='position-absolute' style={`top: ${buttonTop}px; right: ${buttonRight}px; z-index: 1000;`}>
                            <button
                                type='button'
                                class={`btn btn-sm ${buttonClass} rounded-pill me-1`}
                                data-category={category}
                                onClick={this.onClickViewAllArticles}
                            >
                                {/* <i class='fa-solid fa-list text-muted' /> <Text id='dyrejournal.product.all'>All Products</Text> */}
                                <i class='fa-solid fa-list' />
                            </button>
                            <button
                                type='button'
                                class={`btn btn-sm ${buttonClass} rounded-pill`}
                                data-category={category}
                                onClick={this.onClickNewArticle}
                            >
                                {/* <i class='fa-solid fa-list text-muted' /> <Text id='dyrejournal.product.all'>All Products</Text> */}
                                <i class='fa-solid fa-plus' /> {category}
                            </button>
                        </div>
                    </> : <></>}

                    <GuiListTicker
                        key={`article-list-ticker-${tag}-${category}`}
                        classNames={classNames}
                        category={category}
                        styles={isAdmin ? 'border: 0.5px #a0a0a0 dotted;' : ''}
                        stores={this.props.stores}
                        holdingStore={articleStore}
                        view={this.viewArticle}
                        title={showHeader ? (inputTitle ? inputTitle : title) : undefined}
                        list={finalList}
                        // list={[]}
                        elTitle={e => showTitle ? e.title : ''}
                        elLink={e => `/article/${encodeURIComponent(e.title).toLocaleLowerCase()}/${e.uuidv4}`}
                        // elDescription={e => e.ingress}
                        elInfo1={elInfo1}
                        elInfo2={elInfo2}
                        elInfo3={elInfo3}
                        hideEmpty={isAdmin ? false : hideEmpty}
                        editElement={editElement}
                        editFunction={this.editArticle}
                        help={help || children}
                        showFilterBox={false}
                        maxHeight={tickerMaxHeight}
                        minHeight={tickerMinHeight}
                        imgHeight={tickerImgHeight}
                        imgRatio={tickerImgRatio}
                        width={width}
                        viewTags={this.viewTags}
                        showEmptyImage={false}
                        showSoMeStyle={showSoMeStyle}
                    >
                        {isLoggedIn && !isBlockedFromArticles && (userOnly || showCreateButton) ? <>
                            <div class='d-flex flex-row'>
                                <button
                                    type='button'
                                    class={`btn ${buttonClass} rounded-pill`}
                                    data-category={category}
                                    onClick={this.onClickNewArticle}
                                >
                                    <i class='fa-solid fa-plus' /> <Text id='profile.add'>Add</Text>
                                </button>
                            </div>
                        </> : <></>}
                    </GuiListTicker>
                </div>
            </>);
        }

        if (frontpageList) {
            const { filterByTags, selectedAuthor } = this.state;
            const availableTags = this.getAvailableTags(finalList);
            const { tags: storeTags = [], creators: storeCreators = [] } = articleStore;

            // Use creators from API response (stored in articleStore.creators)
            const creators = storeCreators;

            // Merge store tags with article-extracted tags
            const allTags = [...availableTags];
            if (storeTags?.length > 0) {
                storeTags.forEach(tag => {
                    if (tag.name && !allTags.find(t => t.name === tag.name)) {
                        allTags.push(tag);
                    }
                });
            }

            const pendingRows = (this.state.pendingPosts || []).filter(p => !finalList.some(o => o && o.id === p.id));
            const listWithPending = [...pendingRows, ...finalList];

            if (savedOnly && !this.state.loading && listWithPending.length === 0) {
                return (
                    <div class='d-flex flex-column w-100 position-relative'>
                        {this.renderOfflineBanner()}
                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1'>
                            {showHeader ? (inputTitle || title) : null}
                        </div>
                        <div class='d-flex flex-column align-items-center text-center px-4 py-5'>
                            <i class={`fa-solid ${this.state.offlineData ? 'fa-cloud-slash' : 'fa-bookmark'} fs-1 text-muted mb-3`} />
                            <p class='text-muted'>
                                {this.state.offlineData ? (
                                    <Text id='article.saved-offline'>Saved posts are unavailable while offline.</Text>
                                ) : (
                                    <Text id='article.saved-empty'>No saved posts yet.</Text>
                                )}
                            </p>
                            <a href='/feed' class='btn btn-outline-primary rounded-pill'>
                                <i class='fa-solid fa-arrow-left me-2' />
                                <Text id='article.user-feed'>Feed</Text>
                            </a>
                        </div>
                    </div>
                );
            }

            if (personalized && !this.state.loading && listWithPending.length === 0) {
                const { followedAuthors, followedTopics } = this.state.feedPreference;
                const hasPreferences = followedAuthors.length > 0 || followedTopics.length > 0;
                return (
                    <div class='d-flex flex-column w-100 position-relative'>
                        {this.renderOfflineBanner()}
                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between mb-1'>
                            {showHeader ? (inputTitle || title) : null}
                        </div>
                        <div class='d-flex flex-column align-items-center text-center px-4 py-5'>
                            <i class='fa-solid fa-user-check fs-1 text-muted mb-3' />
                            <p class='text-muted'>
                                {hasPreferences ? (
                                    <Text id='article.for-you-empty'>No new posts from what you follow.</Text>
                                ) : (
                                    <Text id='article.for-you-start'>Follow people or topics in the feed to build your own view.</Text>
                                )}
                            </p>
                            <a href='/feed' class='btn btn-outline-primary rounded-pill'>
                                <i class='fa-solid fa-arrow-left me-2' />
                                <Text id='article.user-feed'>Feed</Text>
                            </a>
                        </div>
                    </div>
                );
            }

            const renderBackgroundIngress = this.renderBackgroundIngress;

            return (<>
                <div class='d-flex flex-column w-100 position-relative'>
                    {this.renderOfflineBanner()}
                    {this.renderNewPostsPill()}
                    {isAdmin && !hideAdminButtons ? <>
                        <div class='position-absolute' style={`top: ${buttonTop}px; right: ${buttonRight}px; z-index: 1000;`}>
                            <button
                                type='button'
                                class={`btn btn-sm ${buttonClass} rounded-pill me-1`}
                                data-category={category}
                                onClick={this.onClickViewAllArticles}
                            >
                                {/* <i class='fa-solid fa-list text-muted' /> <Text id='dyrejournal.product.all'>All Products</Text> */}
                                <i class='fa-solid fa-list' />
                            </button>
                            <button
                                type='button'
                                class={`btn btn-sm ${buttonClass} rounded-pill me-1`}
                                data-category={category}
                                onClick={this.onClickNewArticle}
                            >
                                {/* <i class='fa-solid fa-list text-muted' /> <Text id='dyrejournal.product.all'>All Products</Text> */}
                                <i class='fa-solid fa-plus' /> {category || 'Frontpage'}
                            </button>
                        </div>
                    </> : <></>}

                    {/* Filter bar - show when we have tags or creators */}
                    {/* {(allTags.length > 0 || creators.length > 0) && (
                        <div class='position-absolute' style={`top: ${buttonTop}px; left: 15px; z-index: 1000;`}>
                            <ArticleFilterBar
                                availableTags={allTags}
                                selectedTags={filterByTags}
                                creators={creators}
                                selectedAuthor={selectedAuthor}
                                onTagToggle={this.toggleTagFilter}
                                onAuthorSelect={this.onAuthorSelect}
                                onClearFilters={this.onClearFilters}
                                darkmode={darkmode}
                                showAuthorFilter={category === 'feed'}
                            />
                        </div>
                    )} */}

                    {/* {(userOnly || showCreateButton) ? <>
                        <div class={`d-flex flex-row px-3 ${classNames}`}>
                            <button
                                type='button'
                                class={`btn btn-block btn-lg ${buttonClass} rounded-pill`}
                                data-category={category}
                                onClick={this.onClickNewArticle}
                            >
                                <i class='fa-solid fa-plus' /> <Text id='profile.add-new-article'>Add new article</Text>: {category}
                            </button>
                        </div>
                    </> : <></>} */}

                    {category === 'feed' && articleStore.videoProcessingStatus === 'processing' && (
                        <div class='d-flex flex-column px-0 mb-3'>
                            <div class='card border-0 shadow-sm overflow-hidden'>
                                <div class='card-body d-flex align-items-center py-4'>
                                    <div class='spinner-border text-primary me-3' role='status'>
                                        <span class='visually-hidden'>Processing...</span>
                                    </div>
                                    <div>
                                        <h6 class='mb-1'><i class='fa-solid fa-video me-2' /><Text id='article.video-processing'>Processing video...</Text></h6>
                                        <small class='text-body-secondary'>
                                            <Text id='article.video-processing-info'>Your video is being processed. It will appear in the feed when ready.</Text>
                                        </small>
                                    </div>
                                </div>
                                <div class='card-footer bg-transparent border-top-0 py-1'>
                                    <div class='progress' style='height: 3px;'>
                                        <div class='progress-bar progress-bar-striped progress-bar-animated bg-primary' style='width: 100%' />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {category === 'feed' && articleStore.videoProcessingStatus === 'ready' && (
                        <div class='d-flex flex-column px-0 mb-3'>
                            <div class='card border-0 shadow-sm overflow-hidden border-start border-4 border-success'>
                                <div class='card-body d-flex align-items-center py-3'>
                                    <i class='fa-solid fa-circle-check text-success fs-4 me-3' />
                                    <div>
                                        <h6 class='mb-0'><Text id='article.video-ready'>Your video post is now live!</Text></h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {category === 'feed' && articleStore.videoProcessingStatus === 'failed' && (
                        <div class='d-flex flex-column px-0 mb-3'>
                            <div class='card border-0 shadow-sm overflow-hidden border-start border-4 border-warning'>
                                <div class='card-body py-3'>
                                    <div class='d-flex align-items-center mb-2'>
                                        <i class='fa-solid fa-triangle-exclamation text-warning fs-4 me-3' />
                                        <div>
                                            <h6 class='mb-0'><Text id='article.video-failed'>Video processing failed</Text></h6>
                                            <small class='text-body-secondary'>
                                                <Text id='article.video-failed-info'>There was an issue processing your video. You can retry or publish without transcript.</Text>
                                            </small>
                                        </div>
                                    </div>
                                    <div class='d-flex gap-2 ms-5'>
                                        <button type='button' class='btn btn-sm btn-outline-primary rounded-pill' onClick={this.retryVideoProcessing}>
                                            <i class='fa-solid fa-rotate-right me-1' />
                                            <Text id='article.retry'>Retry</Text>
                                        </button>
                                        <button type='button' class='btn btn-sm btn-outline-secondary rounded-pill' onClick={this.publishWithoutTranscript}>
                                            <i class='fa-solid fa-upload me-1' />
                                            <Text id='article.publish-anyway'>Publish without transcript</Text>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <GuiListFrontpage
                        key={`article-list-large-${tag}-${category}`}
                        styles={''}
                        classNames={classNames}
                        category={category}
                        maxHeight={maxHeight}
                        stores={this.props.stores}
                        holdingStore={articleStore}
                        view={this.viewArticle}
                        title={showHeader ? (inputTitle ? inputTitle : title) : undefined}
                        showPadding={false}
                        list={listWithPending}
                        observeElement={this.feedTelemetry ? this.observeFeedElement : undefined}
                        // list={[]}
                        elPendingBadge={this.renderPendingBadge}
                        elHeader={e => e.heading ? <>
                            <span
                                class='mb-0 mt-0 py-1 px-3'
                                style={`font-size: clamp(0.7rem, 2vw, 1.2rem); font-weight: 600; line-height: 1.2em; text-transform: uppercase;`}
                            >
                                {e.headingIcon ? <i class={`${e.headingIcon} me-2`} /> : ''}
                                {e.heading}
                            </span>
                        </> : <></>}
                        elTitle={e => (showTitle || e._feedType === 'news') ? <>
                            <span class={`mb-0 mt-2 py-0 ${e._feedType === 'news' ? 'text-primary' : ''}`}>{e.title}</span>
                        </> : ''}
                        elTitleRight={e => <>
                            {e._isNew === true && (
                                <span class='badge bg-primary ms-2'>
                                    <Text id='article.new-badge'>New</Text>
                                </span>
                            )}
                            {e.requiresMembership === true && (
                                <span class='badge bg-warning text-dark ms-2'>
                                    <i class='fa-solid fa-lock me-1' /><Text id='paywall.badge'>Pluss</Text>
                                </span>
                            )}
                            {e.category === 'marketplace' && <>
                                {e.isSold && <>
                                    <span class='badge text-bg-danger ms-2'>
                                        <i class='fa-duotone fa-tag' /> <Text id='article.sold'>Sold</Text>
                                    </span>
                                </>}
                                <span class='badge text-bg-success ms-2'>
                                    Kr. {util.format(e.price, 2)}
                                </span>
                            </>}
                        </>}
                        elLink={e => `/article/${encodeURIComponent(e.title).toLocaleLowerCase()}/${e.uuidv4}`}
                        elDescription={e => {
                            if (e._feedType === 'news') {
                                const ytRegex = /https?:\/\/(www\.)?youtube\.com\/watch\?v=[a-zA-Z0-9_-]+/g;
                                const cleanIngress = e.ingress ? e.ingress.replace(ytRegex, '').trim() : '';
                                const cleanBody = e.body ? e.body.replace(ytRegex, '').trim() : '';
                                return <>
                                    {this.renderFeedSegments(e, cleanIngress, 'mt-2')}
                                    {this.renderFeedSegments(e, cleanBody)}
                                </>;
                            }
                            const isFeedArticle = e.category === 'feed'
                                || (!e.category && category === 'feed');
                            return <>
                                {isStoryPost(e) ? null : renderBackgroundIngress(e)}
                                {Array.isArray(e.fishCatches) && e.fishCatches.length > 0 && (
                                    <div class='d-flex flex-column gap-2 mt-2'>
                                        {e.fishCatches.map(fishCatch => (
                                            <FishCatchCard key={fishCatch.uuidv4} fishCatch={fishCatch} />
                                        ))}
                                    </div>
                                )}
                                {Array.isArray(e.fishContacts) && e.fishContacts.length > 0 && (
                                    <div class='d-flex flex-column gap-2 mt-2'>
                                        {e.fishContacts.map(fishContact => (
                                            <FishContactCard key={fishContact.uuidv4} fishContact={fishContact} />
                                        ))}
                                    </div>
                                )}
                                {e.ingressTechDetails && <>
                                    <h5 class='mt-3 mb-0'><Text id='input.ingressTechDetails'>Technical details</Text></h5>
                                    <div class='mt-0'><Markdown markdown={e.ingressTechDetails} markedOpts={MARKDOWN_OPTIONS} /></div>
                                </>}
                                {isFeedArticle && this.renderFeedSegments(e, e.body)}
                                {isFeedArticle && e.resultsLists?.length > 0
                                    && !(e.ingress || '').match(/\{\{results\.\d+\}\}/)
                                    && !(e.body || '').match(/\{\{results\.\d+\}\}/)
                                    && e.resultsLists.map((rl, idx) => (
                                        <ResultsList key={`results-fallback-${idx}`} resultsList={rl} stores={this.props.stores} />
                                    ))}
                                {isFeedArticle && e.mapMarkers?.length > 0
                                    && !(e.ingress || '').includes('{{map}}')
                                    && !(e.body || '').includes('{{map}}') && (
                                        <MapFeedPreview article={e} stores={this.props.stores} />
                                    )}
                                {isFeedArticle && e.polls?.length > 0 && e.polls[0]?.id
                                    && !(e.ingress || '').includes('{{poll}}')
                                    && !(e.body || '').includes('{{poll}}') ? (
                                    <Poll
                                        stores={this.props.stores}
                                        poll={e.polls[0]}
                                        onVoteComplete={() => this.loadAll(this.props, false, false)}
                                    />
                                ) : null}
                            </>;
                        }}
                        elOverlayText={e => renderBackgroundIngress(e)}
                        // elInfo1={e => util.formatDate(e.publishDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}
                        // elInfo2={e => showAuthor ? e.creator : ''}
                        elInfo3={e => ''}
                        showBylineHeader={showBylineHeader}
                        showComments={showComments}
                        showLikes={showLikes}
                        showTranslation={showBylineHeader}
                        elAction={e => {
                            const itemCategory = e.category || (!Array.isArray(category) ? category : null);
                            if (!this.isReturnFoundationEnabled()
                                || e._feedType === 'news'
                                || !['feed', 'marketplace'].includes(itemCategory)) {
                                return null;
                            }
                            return (
                                <SaveButton
                                    stores={this.props.stores}
                                    articleId={e.id}
                                    hostname={articleStore.currentHostname || window.location.hostname}
                                    saved={this.state.savedArticleIds.has(Number(e.id))}
                                    surface={personalized ? 'personalized' : (savedOnly ? 'saved' : 'feed')}
                                    visitId={this.feedTelemetry?.visitId}
                                    onSavedChange={this.handleSavedChange}
                                />
                            );
                        }}
                        elAboveLikes={e => {
                            if (e._feedType === 'news' && e.race) {
                                const { newsRaces = {} } = this.state;
                                const race = newsRaces[e.race];
                                if (race) {
                                    return (<>
                                        <div class='px-3 pb-1'>
                                            <button
                                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                                onClick={(ev) => {
                                                    ev.stopPropagation();
                                                    ev.preventDefault();
                                                    route(`/race/${e.race}/news`);
                                                }}
                                            >
                                                <i class='fa-solid fa-flag-checkered me-1' />
                                                {race.title}
                                            </button>
                                        </div>
                                    </>);
                                }
                            }
                            if (e._feedType === 'news' && e.convention) {
                                const { newsConventions = {} } = this.state;
                                const convention = newsConventions[e.convention];
                                if (convention) {
                                    return (<>
                                        <div class='px-3 pb-1'>
                                            <button
                                                class='btn btn-sm btn-outline-secondary rounded-pill'
                                                onClick={(ev) => {
                                                    ev.stopPropagation();
                                                    ev.preventDefault();
                                                    route(`/convention/${e.convention}/news`);
                                                }}
                                            >
                                                <i class='fa-solid fa-calendar-days me-1' />
                                                {convention.title}
                                            </button>
                                        </div>
                                    </>);
                                }
                            }
                            return '';
                        }}
                        elBylineHeader={e => {
                            if (e._feedType === 'news' && e.race) {
                                const { newsRaces = {} } = this.state;
                                const race = newsRaces[e.race];
                                return (<>
                                    <div class={`d-flex flex-column flex-fill py-2 px-2`}>
                                        <div class='d-flex flex-row align-items-center justify-content-start'>
                                            <div class='me-2'>
                                                <Avatar
                                                    name={race?.title || e.creator}
                                                    image={race?.images?.[0]?.s3SmallLink || e.creatorImage}
                                                    // The box describes the creator's photo. When the race image
                                                    // wins the fallback above, centring on it would crop a face
                                                    // that isn't in that picture.
                                                    faceBox={race?.images?.[0]?.s3SmallLink ? null : e.creatorImageFaceBox}
                                                    size={40}
                                                />
                                            </div>
                                            <span class='d-flex flex-column'>
                                                {race && <span class='fw-bold text-body'>{race.title}</span>}
                                                <small class='text-body-secondary'>
                                                    {e.creator && <>{e.creator} &bull; </>}
                                                    {localUtil.getTimeAgo(e.publishDate)}
                                                </small>
                                            </span>
                                        </div>
                                    </div>
                                </>);
                            }
                            if (e._feedType === 'news' && e.convention) {
                                const { newsConventions = {} } = this.state;
                                const convention = newsConventions[e.convention];
                                return (<>
                                    <div class={`d-flex flex-column flex-fill py-2 px-2`}>
                                        <div class='d-flex flex-row align-items-center justify-content-start'>
                                            <div class='me-2'>
                                                <Avatar
                                                    name={convention?.title || e.creator}
                                                    image={convention?.images?.[0]?.s3SmallLink || e.creatorImage}
                                                    // Same as the race branch above: only centre on the face when
                                                    // the creator image is the one actually being shown.
                                                    faceBox={convention?.images?.[0]?.s3SmallLink ? null : e.creatorImageFaceBox}
                                                    size={40}
                                                />
                                            </div>
                                            <span class='d-flex flex-column'>
                                                {convention && <span class='fw-bold text-body'>{convention.title}</span>}
                                                <small class='text-body-secondary'>
                                                    {e.creator && <>{e.creator} &bull; </>}
                                                    {localUtil.getTimeAgo(e.publishDate)}
                                                </small>
                                            </span>
                                        </div>
                                    </div>
                                </>);
                            }
                            const isOwner = user && user.id && e.user === user.id;
                            if (e.user > 0) {
                                presenceStore.track(e.user);
                            }
                            const canViewStats = isAdmin || isOwner;
                            const hasStats = e.totalViews > 0 || e.uniqueViews > 0 || e.totalScrollViews > 0 || e.weeklyStats?.length > 0 || e.hourlyStats?.length > 0;
                            const avgReadTimeFormatted = formatReadTime(e.avgReadTime);
                            const combinedViews = (e.totalViews || 0) + (e.totalScrollViews || 0);
                            // Show stats section if user can view stats (admin always sees it, owner sees if has stats)
                            const showStats = canViewStats && (isAdmin || hasStats);
                            const preferenceReason = personalized ? this.preferenceReasonLabel(e) : '';
                            return (<>
                                <div class={`d-flex flex-column flex-fill py-2 px-2`}>
                                    <div class='d-flex flex-row align-items-center justify-content-start'>
                                        <div
                                            class='d-flex flex-row align-items-center'
                                            style={e.user > 0 ? 'cursor: pointer;' : ''}
                                            onClick={e.user > 0 ? (event) => {
                                                event.stopPropagation();
                                                openUserProfileDrawer(appState, {
                                                    userId: e.user,
                                                    name: e.creator,
                                                    image: e.creatorImage,
                                                    faceBox: e.creatorImageFaceBox,
                                                });
                                            } : undefined}
                                        >
                                            <div class='me-2'>
                                                <Avatar
                                                    name={e.creator}
                                                    image={e.creatorImage}
                                                    faceBox={e.creatorImageFaceBox}
                                                    size={40}
                                                    online={e.user > 0 ? presenceStore.onlineByUserId[e.user] : undefined}
                                                />
                                            </div>
                                            <span class='text-muted font-weight-lighter'>
                                                {e.creator}
                                                <span class='text-muted px-2'>
                                                    •
                                                </span>
                                                {isScheduledArticle(e) ? <>
                                                    <span class='badge text-bg-warning'>
                                                        <i class='fa-solid fa-calendar-clock me-1' />
                                                        <Text id='article-feed.scheduled'>Scheduled</Text> · {scheduledLabel(e.publishDate)}
                                                    </span>
                                                </> : localUtil.getTimeAgo(e.publishDate)}
                                            </span>
                                        </div>
                                        {this.isPersonalizationEnabled()
                                            && this.state.preferenceLoaded
                                            && Number.isInteger(Number(e.user))
                                            && Number(e.user) > 0
                                            && this.isPersonalizableArticle(e) && <FollowButton
                                            stores={this.props.stores}
                                            hostname={articleStore.currentHostname || window.location.hostname}
                                            targetType='author'
                                            targetValue={e.user}
                                            followed={this.state.feedPreference.followedAuthors.includes(Number(e.user))}
                                            surface={this.preferenceSurface()}
                                            showLabel={true}
                                            onPreferenceChange={this.handlePreferenceChange}
                                        />}
                                    </div>
                                    {preferenceReason && <small class='text-body-secondary mt-1 ps-5'>
                                        <i class='fa-solid fa-wand-magic-sparkles me-1' />
                                        <Text id='article.for-you-because'>Because you follow</Text> {preferenceReason}
                                    </small>}
                                    {showStats && <>
                                        <div
                                            class={`d-flex flex-row align-items-center flex-wrap mt-1 ps-1 pb-2`}
                                            style='cursor: pointer;'
                                            role='button'
                                            title='Show detailed statistics'
                                            onClick={(event) => this.openStatsDrawer(event, e.id)}
                                        >
                                            <small class='text-muted me-2'>
                                                <i class='fa-solid fa-chart-simple me-1' />
                                            </small>
                                            <span class='badge bg-light text-muted border me-1' title={`${e.totalViews || 0} page views + ${e.totalScrollViews || 0} scroll views, ${e.uniqueViews || 0} unique`}>
                                                <i class='fa-solid fa-eye me-1' />
                                                {combinedViews}
                                                {e.viewsTrend !== 0 && (
                                                    <span class={`ms-1 ${e.viewsTrend > 0 ? 'text-success' : 'text-danger'}`}>
                                                        {e.viewsTrend > 0 ? '+' : ''}{e.viewsTrend}%
                                                    </span>
                                                )}
                                            </span>
                                            {e.hourlyStats?.length > 0 && (
                                                <span class='badge border me-1 py-1' style='background: transparent;' title={`24-hour activity (${e.hourlyStatsDate || 'today'})`}>
                                                    <Sparkline data={e.hourlyStats} valueField='views' height={14} barWidth={1} barGap={0} maxBars={0} color='#6c757d' />
                                                </span>
                                            )}
                                            {e.weeklyStats?.length > 1 && (
                                                <span class='badge border me-1 py-1' style='background: transparent;' title='Weekly trend'>
                                                    <Sparkline data={e.weeklyStats} valueField='views' height={14} barWidth={2} />
                                                </span>
                                            )}
                                            {(e.totalVideoPlays > 0 || e.totalLinkClicks > 0 || e.totalCarouselScrolls > 0) && <>
                                                {e.totalVideoPlays > 0 && (
                                                    <span class='badge bg-light text-muted border me-1' title={`${e.totalVideoPlays} video plays`}>
                                                        <i class='fa-solid fa-play me-1' />
                                                        {e.totalVideoPlays}
                                                    </span>
                                                )}
                                                {e.totalLinkClicks > 0 && (
                                                    <span class='badge bg-light text-muted border me-1' title={`${e.totalLinkClicks} link clicks`}>
                                                        <i class='fa-solid fa-arrow-up-right-from-square me-1' />
                                                        {e.totalLinkClicks}
                                                    </span>
                                                )}
                                                {e.totalCarouselScrolls > 0 && (
                                                    <span class='badge bg-light text-muted border me-1' title={`${e.totalCarouselScrolls} image carousel scrolls`}>
                                                        <i class='fa-solid fa-images me-1' />
                                                        {e.totalCarouselScrolls}
                                                    </span>
                                                )}
                                            </>}
                                            {avgReadTimeFormatted ? <>
                                                <span class='badge bg-light text-muted border' title={`Average read time: ${avgReadTimeFormatted}`}>
                                                    <i class='fa-solid fa-clock me-1' />
                                                    {avgReadTimeFormatted}
                                                </span>
                                            </> : <>
                                                <span class='badge bg-light text-muted border' title='No read time data yet'>
                                                    <i class='fa-solid fa-clock me-1' />
                                                    --
                                                </span>
                                            </>}
                                        </div>
                                    </>}
                                </div>
                                {isAdmin && (
                                    <div class='d-flex flex-row align-items-center position-relative admin-article-menu'>
                                        <button
                                            class='btn btn-link px-2 py-1'
                                            onClick={(ev) => this.toggleAdminMenu(ev, e.id)}
                                        >
                                            <i class='fa-solid fa-ellipsis-vertical text-body-secondary' />
                                        </button>
                                        {this.state.openMenuId === e.id && (
                                            <div class='position-absolute list-group shadow admin-article-menu' style='top: 100%; right: 0; z-index: 1050; min-width: 240px;'>
                                                <button
                                                    type='button'
                                                    class='list-group-item list-group-item-action'
                                                    onClick={(ev) => this.toggleArticleOffline(ev, e.id)}
                                                >
                                                    <i class='fa-solid fa-eye-slash me-2' /> Ta offline
                                                </button>
                                                <button
                                                    type='button'
                                                    class='list-group-item list-group-item-action'
                                                    onClick={(ev) => this.sendSmsToUser(ev, e)}
                                                >
                                                    <i class='fa-solid fa-message-sms me-2' /> Send SMS til bruker
                                                </button>
                                                <button
                                                    type='button'
                                                    class='list-group-item list-group-item-action text-danger'
                                                    onClick={(ev) => this.showBlockUserConfirm(ev, e)}
                                                >
                                                    <i class='fa-solid fa-ban me-2' /> Blokker bruker
                                                </button>
                                                {this.state.showBlockConfirm === e.id && (
                                                    <div class='p-3 border-top' onClick={(ev) => ev.stopPropagation()}>
                                                        <small class='text-body-secondary d-block mb-2'>
                                                            Blokkerer: <strong>{this.state.blockUserName}</strong>
                                                        </small>
                                                        <small class='text-body-secondary d-block mb-1'>Velg hva som blokkeres:</small>
                                                        {[
                                                            { value: 'articles', label: 'Artikler/Innlegg' },
                                                            { value: 'comments', label: 'Kommentarer & Likes' },
                                                            { value: 'supportTickets', label: 'Support' },
                                                            { value: 'all', label: 'Alt' },
                                                        ].map(feature => (
                                                            <div class='form-check' key={feature.value}>
                                                                <input
                                                                    class='form-check-input'
                                                                    type='checkbox'
                                                                    id={`block-feature-${e.id}-${feature.value}`}
                                                                    checked={this.state.blockFeatures.includes(feature.value)}
                                                                    onChange={(ev) => this.toggleBlockFeature(ev, feature.value)}
                                                                />
                                                                <label class='form-check-label' for={`block-feature-${e.id}-${feature.value}`}>
                                                                    <small>{feature.label}</small>
                                                                </label>
                                                            </div>
                                                        ))}
                                                        <div class='d-flex gap-2 mt-2'>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-danger'
                                                                onClick={this.confirmBlockUser}
                                                                disabled={this.state.blockFeatures.length === 0}
                                                            >
                                                                Bekreft
                                                            </button>
                                                            <button
                                                                type='button'
                                                                class='btn btn-sm btn-secondary'
                                                                onClick={this.cancelBlockUser}
                                                            >
                                                                Avbryt
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>);
                        }}
                        hideEmpty={isAdmin ? false : hideEmpty}
                        editElement={editElement}
                        editFunction={this.editArticle}
                        help={help || children}
                        showFilterBox={false}
                        maxHeight={maxHeight}
                        // imageContainerStyles={'aspect-ratio: 4 / 3'}
                        imageStyles={imageStyles}
                        // imageStyles={'aspect-ratio: 4 / 3'}
                        // showMenu={this.onClickMenu}
                        viewTags={this.viewTags}
                        // loadMore={articleStore.loadMore}

                        listWrapperClassnames={fullWidthOnly ? [] : [
                            'rounded-lg-md',
                            'rounded-md',
                            'rounded-md',
                            'rounded-md',
                            'rounded-md',
                            'rounded-md',
                            'rounded-md',
                            'rounded-md',
                            'rounded-md',
                            'rounded-md',
                        ]}
                        listContainerClassnames={fullWidthOnly ? [] : mergeArrays([
                            'w-100',
                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',

                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',
                            'w-50 px-2',
                        ], articleContainerClasses.map(e => e.class))}
                        listClasses={fullWidthOnly ? [] : mergeArrays([
                            'flex-column',
                            'flex-column',
                            'flex-column',
                            'flex-column',
                            'flex-column',
                            'flex-row',
                            'flex-row',
                            'flex-row',
                            'flex-row',
                            'flex-row',

                            'flex-row',
                            'flex-row',
                            'flex-row',
                            'flex-row',
                        ], articleListClasses.map(e => e.class))}
                        listStyles={[]}
                        listTitleStyles={fullWidthOnly ? [] : [
                            'font-size: clamp(2rem, 4vw, 4rem);   font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',

                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                            'font-size: clamp(0.9rem, 4vw, 2rem); font-weight: 700; line-height: 1.2em;',
                        ]}
                        listMaxImageWidth={fullWidthOnly ? [] : mergeArrays([
                            '100%',
                            '100%',
                            '100%',
                            '100%',
                            '100%',
                            '40%',
                            '40%',
                            '40%',
                            '40%',
                            '40%',

                            '40%',
                            '40%',
                            '40%',
                            '40%',
                        ], articleListImageWidth.map(e => e.width))}
                        listImageLocations={fullWidthOnly ? [] : mergeArrays([
                            'top',
                            'top',
                            'top',
                            'top',
                            'top',
                            'left',
                            'right',
                            'right',
                            'left',
                            'right',

                            'left',
                            'right',
                            'left',
                            'right',
                        ], articleListImageLocations.map(e => e.location))}
                        listWrapperClassNameFull={'w-100'}
                        checkContentHeight={checkContentHeight}
                        showFiles={showFiles}
                    >
                        {isLoggedIn && !isBlockedFromArticles && (userOnly || showCreateButton) ? <>
                            <div class='d-flex flex-row flex-shrink-0'>
                                {category === 'feed' && (
                                    <button
                                        type='button'
                                        class={`btn ${this.state.showScheduled ? 'btn-warning' : 'btn-outline-secondary'} rounded-pill text-nowrap me-2`}
                                        onClick={this.toggleShowScheduled}
                                        title='Vis planlagte innlegg'
                                    >
                                        <i class='fa-solid fa-calendar-clock' /> <span class='d-none d-sm-inline'><Text id='article-feed.show-scheduled'>Scheduled</Text></span>
                                    </button>
                                )}
                                <button
                                    type='button'
                                    class={`btn ${buttonClass} rounded-pill text-nowrap`}
                                    data-category={category}
                                    onClick={this.onClickNewArticle}
                                >
                                    {/* Label is hidden on xs: the header row (tabs + button) exceeds
                                        narrow viewports and #app overflow-x:auto turns that into
                                        page-wide horizontal scroll. */}
                                    <i class='fa-solid fa-plus' /> <span class='d-none d-sm-inline'>{createButtonText ? createButtonText : <Text id='article-feed.add'>Add new</Text>}</span>
                                </button>
                            </div>
                        </> : <></>}
                    </GuiListFrontpage>
                </div>
                {totalArticles > 0 && <>
                    <div class='d-flex flex-row w-100 justify-content-center'>
                        <small class='text-muted'>1 - {totalArticles} articles</small>
                    </div>
                </>}
            </>);
        }

        return (<>
            <div class='d-flex flex-column w-100 mt-5 position-relative'>
                {this.renderOfflineBanner()}
                {isAdmin ? <>
                    <div class='position-absolute' style={`top: ${buttonTop}px; right: ${buttonRight}px; z-index: 1000;`}>
                        <button
                            type='button'
                            class={`btn btn-sm ${buttonClass} rounded-pill me-1`}
                            data-category={category}
                            onClick={this.onClickViewAllArticles}
                        >
                            {/* <i class='fa-solid fa-list text-muted' /> <Text id='dyrejournal.product.all'>All Products</Text> */}
                            <i class='fa-solid fa-list' />
                        </button>
                        <button
                            type='button'
                            class={`btn btn-sm ${buttonClass} rounded-pill me-1`}
                            data-category={category}
                            onClick={this.onClickNewArticle}
                        >
                            {/* <i class='fa-solid fa-list text-muted' /> <Text id='dyrejournal.product.all'>All Products</Text> */}
                            <i class='fa-solid fa-plus' /> {category}
                        </button>
                    </div>
                </> : <></>}

                <GuiList
                    key={`article-list-${tag}-${category}`}
                    styles={isAdmin ? 'border: 0.5px #a0a0a0 dotted;' : ''}
                    classNames={classNames}
                    category={category}
                    stores={this.props.stores}
                    holdingStore={articleStore}
                    view={this.viewArticle}
                    title={showHeader ? (inputTitle ? inputTitle : title) : undefined}
                    list={finalList}
                    elTitle={showTitle ? (elTitle ? elTitle : e => e.title) : () => {}}
                    elLink={e => `/article/${encodeURIComponent(e.title).toLocaleLowerCase()}/${e.uuidv4}`}
                    elInfo1={e => util.formatDate(e.publishDate, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}
                    elInfo2={e => e.creator}
                    elInfo3={e => ''}
                    hideEmpty={isAdmin ? false : hideEmpty}
                    editElement={editElement}
                    editFunction={this.editArticle}
                    help={help || children}
                    showFilterBox={false}
                    maxHeight={'90vh'}
                    viewTags={this.viewTags}

                    swipeRight1Icon={'fa-solid fa-pen'}
                    swipeRight1Class={'primary'}
                    swipeRight1Title={<Text id='article.edit-inline'>Edit</Text>}
                    swipeRight1Func={this.editArticle}

                    // swipeLeft1Icon={'fa-solid fa-check'}
                    swipeLeft1IconFunc={obj => obj.isSold ? 'fa-solid fa-times' : 'fa-solid fa-check'}
                    // swipeLeft1Class={'success'}
                    swipeLeft1ClassFunc={obj => obj.isSold ? 'danger' : 'success'}
                    // swipeLeft1Title={<Text id='article.mark-as-sold'>Mark as sold</Text>}
                    swipeLeft1TitleFunc={obj => obj.isSold ? <Text id='article.mark-as-unsold'>Mark as unsold</Text> : <Text id='article.mark-as-sold'>Mark as sold</Text>}
                    swipeLeft1Func={category === 'marketplace' ? this.markAsSold : undefined}

                    // swipeLeft2Icon={'fa-solid fa-trash'}
                    swipeLeft2IconFunc={obj => obj.active ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'}
                    // swipeLeft2Class={'danger'}
                    swipeLeft2ClassFunc={obj => obj.active ? 'secondary' : 'success'}
                    // swipeLeft2Title={<Text id='article.delete'>Delete</Text>}
                    swipeLeft2TitleFunc={obj => obj.active ? <Text id='article.unpublish'>Unpublish</Text> : <Text id='article.publish'>Publish</Text>}
                    swipeLeft2Func={this.deleteArticle}

                >
                    {isLoggedIn && !isBlockedFromArticles && (userOnly || showCreateButton) ? <>
                        <div class='d-flex flex-row'>
                            <button
                                type='button'
                                class={`btn  ${buttonClass} rounded-pill`}
                                data-category={category}
                                onClick={this.onClickNewArticle}
                            >
                                <i class='fa-solid fa-plus' /> <Text id='article-feed.add'>Add new post</Text>
                            </button>
                        </div>
                    </> : <></>}
                </GuiList>

            </div>

        </>);
    }
}

export default ArticleList;
